import React, { useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Tooltip, DatePicker, Tabs, Input } from "antd";
import dayjs from "dayjs";
import {
  Mail,
  Plus,
  Search,
  Users,
  Send,
  CheckCircle2,
  ArrowRight,
  RotateCw,
  Play,
  Pause,
  XCircle,
  Info,
  BarChart3,
} from "lucide-react";
import {
  getMailMergeCampaigns,
  getMailMergeStats,
  startMailMergeCampaign,
  pauseMailMergeCampaign,
  cancelMailMergeCampaign,
} from "../services/mailMergeService";
import EmailForm from "../common/EmailForm";
import { formatDateTime } from "../utils";

const MailMergeListing = () => {
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState([]);
  const [totalElements, setTotalElements] = useState(0);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [activeTab, setActiveTab] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [dateRange, setDateRange] = useState(null);
  const pageSize = 8;
  const [stats, setStats] = useState({
    totalCampaigns: 0,
    totalSent: 0,
    avgDeliveryRate: 0,
    completedCount: 0,
    inProgressCount: 0
  });

  const rangePresets = [
    { label: "Today", value: [dayjs().startOf("day"), dayjs().endOf("day")] },
    {
      label: "Yesterday",
      value: [
        dayjs().subtract(1, "day").startOf("day"),
        dayjs().subtract(1, "day").endOf("day"),
      ],
    },
    {
      label: "Last 7 Days",
      value: [dayjs().subtract(6, "day").startOf("day"), dayjs().endOf("day")],
    },
    {
      label: "Last 30 Days",
      value: [
        dayjs().subtract(29, "day").startOf("day"),
        dayjs().endOf("day"),
      ],
    },
    {
      label: "This Month",
      value: [dayjs().startOf("month"), dayjs().endOf("month")],
    },
    {
      label: "Last Month",
      value: [
        dayjs().subtract(1, "month").startOf("month"),
        dayjs().subtract(1, "month").endOf("month"),
      ],
    },
  ];

  const fetchStats = async () => {
    try {
      const data = await getMailMergeStats();
      const totalSent = data.totalSent || 0;
      const totalDelivered = data.totalDelivered || 0;
      const avgDeliveryRate = totalSent > 0 ? Math.round((totalDelivered / totalSent) * 100) : 0;
      setStats({
        totalCampaigns: data.totalCampaigns || 0,
        totalSent,
        avgDeliveryRate,
        completedCount: data.completedCount || 0,
        inProgressCount: data.inProgressCount || 0
      });
    } catch (err) {
      console.error(err);
    }
  };

  const fetchCampaigns = async () => {
    setLoading(true);
    try {
      const params = {
        page: currentPage - 1,
        size: pageSize,
        sort: "createdAt,desc",
      };
      if (searchTerm.trim()) {
        params["subject.contains"] = searchTerm.trim();
      }
      if (activeTab === "completed") {
        params["status.equals"] = "COMPLETED";
      } else if (activeTab === "in_progress") {
        params["status.equals"] = "RUNNING";
      }
      if (dateRange && dateRange[0] && dateRange[1]) {
        params["createdAt.greaterThanOrEqual"] =
          dateRange[0].format("YYYY-MM-DD") + "T00:00:00Z";
        params["createdAt.lessThanOrEqual"] =
          dateRange[1].format("YYYY-MM-DD") + "T23:59:59Z";
      }
      const data = await getMailMergeCampaigns(params);
      setCampaigns(data.content || []);
      setTotalElements(data.totalElements || 0);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
  }, []);

  useEffect(() => {
    fetchCampaigns();
  }, [currentPage, searchTerm, activeTab, dateRange]);

  const totalPages = Math.ceil(totalElements / pageSize) || 1;

  const renderCampaignStatusBadge = (c) => {
    const status = c?.status?.toLowerCase();
    if (
      status === "failed" ||
      status === "bounced" ||
      status === "limit exceed" ||
      status === "limit exceeded" ||
      status === "limit_exceeded"
    ) {
      const infoMessage =
        c?.failureReason ||
        c?.errorMessage ||
        c?.reason ||
        c?.error ||
        c?.statusMessage ||
        c?.info ||
        c?.message ||
        "Campaign failed. Check configuration or try again later.";

      return (
        <span className="tw-inline-flex tw-items-center tw-gap-1.5 tw-px-2.5 tw-py-0.5 tw-rounded-md tw-text-[11px] tw-font-semibold tw-bg-red-50 tw-text-red-700 tw-font-inter">
          <span>{c.status.charAt(0).toUpperCase() + c.status.slice(1).toLowerCase()}</span>
          <Tooltip title={infoMessage} placement="top">
            <span className="tw-inline-flex tw-items-center tw-cursor-pointer tw-text-red-500 hover:tw-text-red-700 tw-transition-colors">
              <Info size={12} className="tw-stroke-[2.5]" />
            </span>
          </Tooltip>
        </span>
      );
    }

    if (status === "running") {
      return (
        <span className="tw-inline-flex tw-items-center tw-px-2.5 tw-py-0.5 tw-rounded-md tw-text-[11px] tw-font-semibold tw-bg-[#EBF3FF] tw-text-[#2E90FA] tw-font-inter">
          Running
        </span>
      );
    }

    if (status === "paused") {
      return (
        <span className="tw-inline-flex tw-items-center tw-px-2.5 tw-py-0.5 tw-rounded-md tw-text-[11px] tw-font-semibold tw-bg-amber-50 tw-text-amber-700 tw-font-inter">
          Paused
        </span>
      );
    }

    return (
      <span className="tw-inline-flex tw-items-center tw-px-2.5 tw-py-0.5 tw-rounded-md tw-text-[11px] tw-font-semibold tw-bg-[#ECFDF5] tw-text-[#007A55] tw-font-inter">
        {c?.status ? (c.status.charAt(0).toUpperCase() + c.status.slice(1).toLowerCase()) : "Completed"}
      </span>
    );
  };

  const mailMergeTabs = [
    {
      key: "all",
      label: "All Campaigns",
      count: stats.totalCampaigns,
      tooltip: "View all mail merge email campaigns",
    },
    {
      key: "completed",
      label: "Completed",
      count: stats.completedCount,
      tooltip: "View completed email campaigns",
    },
    {
      key: "in_progress",
      label: "In Progress",
      count: stats.inProgressCount,
      tooltip: "View active campaigns currently sending",
    },
  ];

  return (
    <div className="p-20 tw-font-inter tw-bg-white tw-min-h-full tw-overflow-y-auto">
      <div className="tw-max-w-[1400px] tw-mx-auto">
        {/* Top Header */}
        <div className="tw-flex tw-flex-col sm:tw-flex-row sm:tw-items-center sm:tw-justify-between tw-gap-4 tw-mb-6">
          <div>
            <h1 className="tw-text-xl tw-font-bold tw-text-slate-800 tw-font-inter tw-tracking-tight tw-mb-1">
              Mail Merge Campaigns
            </h1>
            <p className="tw-text-xs tw-text-slate-500 tw-font-medium tw-font-inter">
              Track and manage all your bulk email outreach campaigns and performance.
            </p>
          </div>

          {/* New Mail Merge Button */}
          <EmailForm>
            <button
              type="button"
              className="tw-inline-flex tw-items-center tw-gap-2 tw-px-4 tw-py-2.5 tw-rounded-xl tw-bg-[#5D44FF] hover:tw-bg-[#4e36f0] active:tw-bg-[#4e36f0] tw-text-white tw-font-semibold tw-text-xs tw-shadow-sm tw-transition-all tw-border-0 tw-cursor-pointer tw-font-inter active:tw-scale-95"
            >
              <Plus size={15} className="tw-stroke-[2.5]" />
              <span>New Mail Merge</span>
            </button>
          </EmailForm>
        </div>

        {/* Main Content Grid: Left Side Tabs & Table (col-span-8/9), Right Side Overview Card (col-span-4/3) */}
        <div className="tw-grid tw-grid-cols-1 lg:tw-grid-cols-12 tw-gap-6 tw-items-start">
          {/* Left Column: Status Filter Tabs + Table Card */}
          <div className="lg:tw-col-span-8 xl:tw-col-span-9">
            {/* Status Filter Tabs with Search & Date Filter on Right matching Campaign & Search tab design */}
            <div id="tabscompany" className="tw-mb-4">
          <Tabs
            activeKey={activeTab}
            onChange={(key) => {
              setActiveTab(key);
              setCurrentPage(1);
            }}
            tabBarExtraContent={{
              right: (
                <div className="tw-flex tw-items-center tw-gap-3 tw-pb-1 tw-flex-wrap">
                  <Input
                    value={searchTerm}
                    onChange={(e) => {
                      setSearchTerm(e.target.value);
                      setCurrentPage(1);
                    }}
                    prefix={<Search className="tw-text-slate-400 tw-w-4 tw-h-4" />}
                    placeholder="Search by campaign name or subject..."
                    className="!tw-h-[38px] !tw-rounded-xl tw-border-slate-200 hover:tw-border-slate-300 focus:tw-border-[#5D44FF] tw-w-[200px] sm:tw-w-[260px] tw-font-inter tw-text-xs"
                    allowClear
                  />
                  <DatePicker.RangePicker
                    value={dateRange}
                    onChange={(dates) => {
                      setDateRange(dates);
                      setCurrentPage(1);
                    }}
                    presets={rangePresets}
                    format="MMM DD, YYYY"
                    placeholder={["Start date", "End date"]}
                    className="!tw-h-[38px] !tw-rounded-xl tw-border-slate-200/80 tw-text-xs tw-font-inter hover:tw-border-[#5D44FF] focus:tw-border-[#5D44FF] tw-shadow-2xs [&_.ant-picker-input>input]:tw-text-xs [&_.ant-picker-input>input]:tw-font-inter"
                  />
                </div>
              ),
            }}
            className="tw-font-inter !tw-mb-0 [&_.ant-tabs-nav]:!tw-mb-0 [&_.ant-tabs-nav]:!tw-w-full"
            items={mailMergeTabs.map((tab) => {
              const isActive = activeTab === tab.key;
              return {
                key: tab.key,
                label: (
                  <Tooltip title={tab.tooltip}>
                    <span className="tw-flex tw-items-center tw-gap-2 tw-text-xs sm:tw-text-sm tw-font-semibold tw-py-1">
                      <span>{tab.label}</span>
                      <span
                        className={`tw-inline-flex tw-items-center tw-justify-center tw-px-2 tw-py-0.5 tw-text-[11px] tw-font-bold tw-rounded-full tw-transition-colors ${
                          isActive
                            ? "tw-bg-[#EFEAFF] tw-text-[#5D44FF]"
                            : "tw-bg-slate-100 tw-text-slate-500"
                        }`}
                      >
                        {tab.count}
                      </span>
                    </span>
                  </Tooltip>
                ),
              };
            })}
          />
        </div>

        {/* Main Table Card */}
        <div className="tw-bg-white tw-border tw-border-solid tw-border-slate-200/80 tw-rounded-2xl tw-overflow-hidden tw-shadow-sm">

          {/* Table */}
          <div className="tw-overflow-x-auto">
            <table className="tw-w-full tw-border-collapse tw-text-left">
              <thead>
                <tr className="tw-bg-white tw-border-b tw-border-t-0 tw-border-l-0 tw-border-r-0 tw-border-solid tw-border-slate-100 tw-text-[11px] tw-font-bold tw-text-slate-400 tw-uppercase tw-tracking-wider tw-font-inter">
                  <th className="tw-py-3.5 tw-pl-6">CAMPAIGN & SUBJECT</th>
                  <th className="tw-py-3.5 tw-px-4">SENT DATE</th>
                  <th className="tw-py-3.5 tw-px-4">RECIPIENTS</th>
                  <th className="tw-py-3.5 tw-px-4">DELIVERY RATE</th>
                  <th className="tw-py-3.5 tw-px-4">STATUS</th>
                  <th className="tw-py-3.5 tw-pr-6 tw-text-right">ACTIONS</th>
                </tr>
              </thead>
              <tbody className="tw-divide-y tw-divide-slate-50">
                {campaigns.length === 0 ? (
                  <tr>
                    <td
                      colSpan={6}
                      className="tw-text-center tw-py-12 tw-text-xs tw-text-slate-400 tw-font-inter"
                    >
                      No mail merge campaigns found.
                    </td>
                  </tr>
                ) : (
                  campaigns.map((c) => {
                    const rate = c.recipientsCount
                      ? Math.round(((c.sentCount || 0) / c.recipientsCount) * 100)
                      : 0;

                    return (
                      <tr
                        key={c.id}
                        className="hover:tw-bg-slate-50/60 tw-transition-colors group"
                      >
                        {/* Campaign Name & Subject */}
                        <td className="tw-py-4 tw-pl-6 tw-max-w-xs">
                          <button
                            type="button"
                            onClick={() => navigate(`/mail-merge/${c.id}`)}
                            className="tw-text-left tw-bg-transparent tw-border-0 tw-p-0 tw-cursor-pointer tw-block tw-w-full"
                          >
                            <div className="tw-text-xs tw-font-bold tw-text-slate-800 group-hover:tw-text-[#5D44FF] tw-transition-colors tw-truncate tw-font-inter">
                              {c.name}
                            </div>
                            <div className="tw-text-[11px] tw-text-slate-500 tw-truncate tw-mt-0.5 tw-font-inter">
                              {c.subject}
                            </div>
                          </button>
                        </td>

                        {/* Sent Date */}
                        <td className="tw-py-4 tw-px-4 tw-text-xs tw-text-slate-500 tw-whitespace-nowrap tw-font-inter">
                          {formatDateTime(c.sentAt || c.createdAt)}
                        </td>

                        {/* Recipients */}
                        <td className="tw-py-4 tw-px-4 tw-text-xs tw-text-slate-700 tw-font-semibold tw-whitespace-nowrap tw-font-inter">
                          <div className="tw-flex tw-items-center tw-gap-1.5">
                            <Users size={13} className="tw-text-slate-400" />
                            <span>{c.recipientsCount}</span>
                          </div>
                        </td>

                        {/* Delivery Rate */}
                        <td className="tw-py-4 tw-px-4 tw-min-w-[140px]">
                          <div className="tw-flex tw-items-center tw-gap-2.5">
                            <div className="tw-flex-1 tw-h-1.5 tw-bg-slate-100 tw-rounded-full tw-overflow-hidden">
                              <div
                                style={{ width: `${rate}%` }}
                                className="tw-h-full tw-bg-[#5D44FF] tw-rounded-full"
                              />
                            </div>
                            <span className="tw-text-xs tw-font-bold tw-text-slate-700 tw-font-inter">
                              {rate}%
                            </span>
                          </div>
                        </td>

                        {/* Status */}
                        <td className="tw-py-4 tw-px-4">
                          {renderCampaignStatusBadge(c)}
                        </td>

                        {/* Actions */}
                        <td className="tw-py-4 tw-pr-6 tw-text-right">
                          <div className="tw-flex tw-items-center tw-justify-end tw-gap-2">
                            {c.status === "PAUSED" && (
                              <button
                                type="button"
                                onClick={async () => {
                                  await startMailMergeCampaign(c.id);
                                  fetchCampaigns();
                                  fetchStats();
                                }}
                                className="tw-p-1.5 tw-rounded-lg tw-border tw-border-solid tw-border-slate-200 tw-bg-white hover:tw-bg-slate-50 tw-text-slate-500 hover:tw-text-[#12B76A] tw-transition-all tw-cursor-pointer"
                                title="Resume Campaign"
                              >
                                <Play size={13} />
                              </button>
                            )}
                            {(c.status === "RUNNING" || c.status === "CREATED") && (
                              <button
                                type="button"
                                onClick={async () => {
                                  await pauseMailMergeCampaign(c.id);
                                  fetchCampaigns();
                                  fetchStats();
                                }}
                                className="tw-p-1.5 tw-rounded-lg tw-border tw-border-solid tw-border-slate-200 tw-bg-white hover:tw-bg-slate-50 tw-text-slate-500 hover:tw-text-[#F59E0B] tw-transition-all tw-cursor-pointer"
                                title="Pause Campaign"
                              >
                                <Pause size={13} />
                              </button>
                            )}
                            {(c.status === "RUNNING" || c.status === "CREATED" || c.status === "PAUSED") && (
                              <button
                                type="button"
                                onClick={async () => {
                                  if (window.confirm("Are you sure you want to cancel this campaign?")) {
                                    await cancelMailMergeCampaign(c.id);
                                    fetchCampaigns();
                                    fetchStats();
                                  }
                                }}
                                className="tw-p-1.5 tw-rounded-lg tw-border tw-border-solid tw-border-slate-200 tw-bg-white hover:tw-bg-slate-50 tw-text-slate-500 hover:tw-text-red-500 tw-transition-all tw-cursor-pointer"
                                title="Cancel Campaign"
                              >
                                <XCircle size={13} />
                              </button>
                            )}
                            <button
                              type="button"
                              onClick={() => navigate(`/mail-merge/${c.id}`)}
                              className="tw-px-3 tw-py-1.5 tw-rounded-lg tw-border tw-border-solid tw-border-slate-200 tw-bg-white hover:tw-bg-slate-50 tw-text-slate-700 tw-font-semibold tw-text-xs tw-flex tw-items-center tw-gap-1.5 tw-transition-all tw-cursor-pointer tw-font-inter"
                              title="View Campaign Details"
                            >
                              <span>Report</span>
                              <ArrowRight size={12} />
                            </button>

                            <EmailForm
                              payload={{
                                subject: c.subject,
                                body: c.bodyTemplate || c.body || c.content || "",
                                bodyTemplate: c.bodyTemplate || c.body || c.content || "",
                                campaignId: c.id,
                              }}
                              recipients={c.recipients?.map((r) => ({
                                name: r.name,
                                email: r.email,
                              }))}
                            >
                              <button
                                type="button"
                                className="tw-p-1.5 tw-rounded-lg tw-border tw-border-solid tw-border-slate-200 tw-bg-white hover:tw-bg-slate-50 tw-text-slate-500 hover:tw-text-[#5D44FF] tw-transition-all tw-cursor-pointer"
                                title="Reuse this email"
                              >
                                <RotateCw size={13} />
                              </button>
                            </EmailForm>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Footer / Pagination */}
          <div className="tw-px-6 tw-py-4 tw-flex tw-flex-col sm:tw-flex-row sm:tw-items-center sm:tw-justify-between tw-gap-3 tw-border-t tw-border-b-0 tw-border-l-0 tw-border-r-0 tw-border-solid tw-border-slate-100 tw-text-xs tw-text-slate-500 tw-font-inter">
            <div>
              Showing{" "}
              <span className="tw-font-bold tw-text-slate-700">
                {totalElements > 0
                  ? (currentPage - 1) * pageSize + 1
                  : 0}
              </span>{" "}
              to{" "}
              <span className="tw-font-bold tw-text-slate-700">
                {Math.min(currentPage * pageSize, totalElements)}
              </span>{" "}
              of{" "}
              <span className="tw-font-bold tw-text-slate-700">
                {totalElements}
              </span>{" "}
              campaigns
            </div>

            <div className="tw-flex tw-items-center tw-gap-2">
              <button
                type="button"
                disabled={currentPage <= 1}
                onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                className="tw-px-3.5 tw-py-1.5 tw-rounded-lg tw-border tw-border-solid tw-border-slate-200 tw-bg-white hover:tw-bg-slate-50 disabled:tw-opacity-40 disabled:tw-cursor-not-allowed tw-text-slate-700 tw-font-semibold tw-text-xs tw-transition-all tw-cursor-pointer tw-font-inter"
              >
                Prev
              </button>
              <button
                type="button"
                disabled={currentPage >= totalPages}
                onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
                className="tw-px-3.5 tw-py-1.5 tw-rounded-lg tw-border tw-border-solid tw-border-slate-200 tw-bg-white hover:tw-bg-slate-50 disabled:tw-opacity-40 disabled:tw-cursor-not-allowed tw-text-slate-700 tw-font-semibold tw-text-xs tw-transition-all tw-cursor-pointer tw-font-inter"
              >
                Next
              </button>
            </div>
          </div>
        </div>
      </div>

          {/* Right Column: Performance Overview Card */}
          <div className="lg:tw-col-span-4 xl:tw-col-span-3">
            <div className="tw-bg-white tw-border tw-border-solid tw-border-slate-200/80 tw-rounded-2xl tw-p-5 tw-shadow-sm tw-sticky tw-top-4">
              {/* Card Header */}
              <div className="tw-flex tw-items-center tw-justify-between tw-pb-3.5 tw-mb-4 tw-border-b tw-border-t-0 tw-border-l-0 tw-border-r-0 tw-border-solid tw-border-slate-100">
                <div>
                  <h3 className="tw-text-sm tw-font-bold tw-text-slate-800 tw-font-inter tw-m-0">
                    Campaign Overview
                  </h3>
                  <p className="tw-text-[11px] tw-text-slate-400 tw-m-0 tw-mt-0.5 tw-font-medium">
                    Outreach & delivery metrics
                  </p>
                </div>
                <div className="tw-size-8 tw-rounded-xl tw-bg-[#EEF0FF] tw-flex tw-items-center tw-justify-center tw-text-[#5D44FF]">
                  <BarChart3 size={16} />
                </div>
              </div>

              {/* Metric 1: Total Campaigns */}
              <div className="tw-p-3.5 tw-rounded-xl tw-bg-slate-50/80 tw-border tw-border-solid tw-border-slate-100 tw-mb-3">
                <div className="tw-flex tw-items-center tw-justify-between tw-mb-1">
                  <span className="tw-text-[11px] tw-font-bold tw-text-slate-400 tw-uppercase tw-tracking-wider tw-font-inter">
                    Total Campaigns
                  </span>
                  <span className="tw-text-lg tw-font-bold tw-text-slate-800 tw-font-inter">
                    {stats.totalCampaigns}
                  </span>
                </div>
                <div className="tw-flex tw-items-center tw-gap-2 tw-mt-2 tw-pt-2 tw-border-t tw-border-t-slate-200/60 tw-border-b-0 tw-border-l-0 tw-border-r-0 tw-border-solid tw-text-[11px] tw-text-slate-500">
                  <span className="tw-inline-flex tw-items-center tw-gap-1">
                    <span className="tw-size-1.5 tw-rounded-full tw-bg-[#12B76A]" />
                    <span>{stats.completedCount} Completed</span>
                  </span>
                  <span className="tw-text-slate-300">•</span>
                  <span className="tw-inline-flex tw-items-center tw-gap-1">
                    <span className="tw-size-1.5 tw-rounded-full tw-bg-[#2E90FA]" />
                    <span>{stats.inProgressCount} In Progress</span>
                  </span>
                </div>
              </div>

              {/* Metric 2: Total Emails Sent */}
              <div className="tw-p-3.5 tw-rounded-xl tw-bg-slate-50/80 tw-border tw-border-solid tw-border-slate-100 tw-mb-3">
                <div className="tw-flex tw-items-center tw-justify-between tw-mb-1">
                  <span className="tw-text-[11px] tw-font-bold tw-text-slate-400 tw-uppercase tw-tracking-wider tw-font-inter">
                    Total Emails Sent
                  </span>
                  <span className="tw-text-lg tw-font-bold tw-text-slate-800 tw-font-inter">
                    {stats.totalSent}
                  </span>
                </div>
                <div className="tw-text-[11px] tw-text-slate-400 tw-font-medium">
                  Outreach emails delivered to leads
                </div>
              </div>

              {/* Metric 3: Avg Delivery Rate */}
              <div className="tw-p-3.5 tw-rounded-xl tw-bg-slate-50/80 tw-border tw-border-solid tw-border-slate-100 tw-mb-0">
                <div className="tw-flex tw-items-center tw-justify-between tw-mb-1.5">
                  <span className="tw-text-[11px] tw-font-bold tw-text-slate-400 tw-uppercase tw-tracking-wider tw-font-inter">
                    Avg Delivery Rate
                  </span>
                  <span className="tw-text-lg tw-font-bold tw-text-slate-800 tw-font-inter">
                    {stats.avgDeliveryRate}%
                  </span>
                </div>
                {/* Progress bar */}
                <div className="tw-w-full tw-h-2 tw-bg-slate-200/70 tw-rounded-full tw-overflow-hidden tw-mt-2">
                  <div
                    style={{ width: `${Math.min(100, Math.max(0, stats.avgDeliveryRate))}%` }}
                    className="tw-h-full tw-bg-[#5D44FF] tw-rounded-full tw-transition-all"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MailMergeListing;
